// Unified selection state shared by Dashboard, History, Logs.
//
//   selectedMachines  – which machines are in scope (max 6)
//   viewMode          – 'linked' (one pin set, applied everywhere) or
//                       'independent' (per-machine pin sets)
//   linkedPins        – the global pin list (used in linked mode)
//   independentPins   – per-machine pin lists (used in independent mode)
//
// Pages still own their page-specific config (preset, range, layout etc.)
// but the *machine selection* and *sensor pin set* travel with the user.

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ViewMode } from '@/types';

const MAX_MACHINES = 6;

const EMPTY: string[] = [];

export interface ViewState {
  selectedMachines: string[];
  viewMode:         ViewMode;
  linkedPins:       string[];
  independentPins:  Record<string, string[]>;
  /** Per-machine card chart visibility. Sparse map: presence = hidden.
   *  Default-on semantics so a missing key renders the chart. Keyed by
   *  the same machineKey ("company.machine") used everywhere else. */
  hiddenCharts:     Record<string, true>;
  /** Per-(machine, field) sensor-row visibility on the dashboard card
   *  table (Sprint D). Sparse map: presence = hidden. Key format
   *  `${machineKey}::${field}`. Per-machine, NOT linked-aware — hiding a
   *  sensor on machine A never blanks the same field on machine B, even
   *  in linked view mode. Hide = "focused elsewhere right now"; pinning
   *  stays the linked "I always want this" decision. */
  hiddenSeries:     Record<string, true>;
  /** Generic collapse map for any "window" the user can collapse: panes
   *  (history.charts, history.band, history.table), per-machine history
   *  rows (history.row.<machineKey>), or any future panel. Sparse:
   *  presence = collapsed. Default-on for everything not listed. */
  collapsedWindows: Record<string, true>;

  toggleMachine:      (key: string, max?: number) => void;
  setMachines:        (keys: string[]) => void;
  setViewMode:        (m: ViewMode) => void;
  togglePin:          (field: string, machineKey?: string) => void;
  setLinkedPins:      (fields: string[]) => void;
  setIndependentPins: (machineKey: string, fields: string[]) => void;
  toggleChart:        (machineKey: string) => void;
  toggleSeries:       (machineKey: string, field: string) => void;
  /** Bulk show/hide every given field for a machine in one update. */
  setSeriesHiddenAll: (machineKey: string, fields: string[], hidden: boolean) => void;
  toggleWindow:       (windowKey: string) => void;
  setWindowCollapsed: (windowKey: string, collapsed: boolean) => void;
}

/** Compose the sparse-map key for a hidden sensor row. */
export function seriesHideKey(machineKey: string, field: string): string {
  return `${machineKey}::${field}`;
}

function toggleIn(list: string[], item: string): string[] {
  return list.includes(item) ? list.filter((x) => x !== item) : [...list, item];
}

export const useViewStore = create<ViewState>()(
  persist(
    (set, get) => ({
      selectedMachines: [],
      viewMode:         'linked',
      linkedPins:       ['LaserPower_ACT', 'KuehlerTemp'],
      independentPins:  {},
      hiddenCharts:     {},
      hiddenSeries:     {},
      collapsedWindows: {},

      toggleMachine: (key, max = MAX_MACHINES) => {
        const sel = get().selectedMachines;
        if (sel.includes(key)) set({ selectedMachines: sel.filter((k) => k !== key) });
        else if (sel.length < max) set({ selectedMachines: [...sel, key] });
      },

      setMachines: (keys) => set({ selectedMachines: keys.slice(0, MAX_MACHINES) }),

      setViewMode: (viewMode) => set({ viewMode }),

      togglePin: (field, machineKey) => {
        const { viewMode, linkedPins, independentPins } = get();
        if (viewMode === 'linked') {
          set({ linkedPins: toggleIn(linkedPins, field) });
        } else if (machineKey) {
          const cur = independentPins[machineKey] ?? EMPTY;
          set({ independentPins: { ...independentPins, [machineKey]: toggleIn(cur, field) } });
        }
      },

      setLinkedPins: (fields) => set({ linkedPins: fields }),

      setIndependentPins: (machineKey, fields) => {
        const { independentPins } = get();
        set({ independentPins: { ...independentPins, [machineKey]: fields } });
      },

      toggleChart: (machineKey) => {
        const cur = get().hiddenCharts;
        if (cur[machineKey]) {
          const { [machineKey]: _drop, ...rest } = cur;
          set({ hiddenCharts: rest });
        } else {
          set({ hiddenCharts: { ...cur, [machineKey]: true } });
        }
      },

      toggleSeries: (machineKey, field) => {
        const key = seriesHideKey(machineKey, field);
        const cur = get().hiddenSeries;
        if (cur[key]) {
          const { [key]: _drop, ...rest } = cur;
          set({ hiddenSeries: rest });
        } else {
          set({ hiddenSeries: { ...cur, [key]: true } });
        }
      },

      setSeriesHiddenAll: (machineKey, fields, hidden) => {
        const cur = get().hiddenSeries;
        const next: Record<string, true> = { ...cur };
        for (const f of fields) {
          const key = seriesHideKey(machineKey, f);
          if (hidden) next[key] = true;
          else delete next[key];
        }
        set({ hiddenSeries: next });
      },

      toggleWindow: (windowKey) => {
        const cur = get().collapsedWindows;
        if (cur[windowKey]) {
          const { [windowKey]: _drop, ...rest } = cur;
          set({ collapsedWindows: rest });
        } else {
          set({ collapsedWindows: { ...cur, [windowKey]: true } });
        }
      },

      setWindowCollapsed: (windowKey, collapsed) => {
        const cur = get().collapsedWindows;
        if (collapsed) {
          if (cur[windowKey]) return;
          set({ collapsedWindows: { ...cur, [windowKey]: true } });
        } else {
          if (!cur[windowKey]) return;
          const { [windowKey]: _drop, ...rest } = cur;
          set({ collapsedWindows: rest });
        }
      },
    }),
    {
      name:    'lc-view-cfg',
      version: 3,
      // v1 → v2: add hiddenCharts + collapsedWindows. Drop any malformed
      // values from persisted state (defensive — older builds never set
      // them, but a corrupted localStorage payload could carry non-object
      // entries that would break the Record<string,true> reads).
      // v2 → v3: add hiddenSeries (Sprint D per-row sensor hide).
      migrate: (state: unknown, fromVersion: number): ViewState => {
        let s = (state as Partial<ViewState>) ?? ({} as Partial<ViewState>);
        if (fromVersion < 2) {
          const hiddenCharts =
            s.hiddenCharts && typeof s.hiddenCharts === 'object' ? s.hiddenCharts : {};
          const collapsedWindows =
            s.collapsedWindows && typeof s.collapsedWindows === 'object' ? s.collapsedWindows : {};
          s = { ...(s as ViewState), hiddenCharts, collapsedWindows };
        }
        if (fromVersion < 3) {
          const hiddenSeries =
            s.hiddenSeries && typeof s.hiddenSeries === 'object' ? s.hiddenSeries : {};
          s = { ...(s as ViewState), hiddenSeries };
        }
        return s as ViewState;
      },
    },
  ),
);

/** Reactive primitive selector for "is this card's chart hidden?". Pulls
 *  a boolean off the sparse map so the subscriber re-renders only when
 *  THIS machine's flag flips, not when any other machine toggles. */
export function useChartHidden(machineKey: string): boolean {
  return useViewStore((s) => s.hiddenCharts[machineKey] === true);
}

/** Same shape for collapsedWindows. Primitive boolean → stable identity
 *  so memoized children don't churn. */
export function useWindowCollapsed(windowKey: string): boolean {
  return useViewStore((s) => s.collapsedWindows[windowKey] === true);
}

/** Reactive "is this (machine, field) sensor row hidden?". Primitive
 *  boolean so a subscriber only re-renders when ITS row flips. */
export function useSeriesHidden(machineKey: string, field: string): boolean {
  return useViewStore((s) => s.hiddenSeries[seriesHideKey(machineKey, field)] === true);
}

/** Stable empty pins reference. Returned when no per-machine pins exist. */
export const NO_PINS: string[] = EMPTY;

/** Resolve effective pins for a given machine, respecting view mode. */
export function useResolvedPins(machineKey: string): string[] {
  return useViewStore((s) =>
    s.viewMode === 'linked' ? s.linkedPins : (s.independentPins[machineKey] ?? NO_PINS),
  );
}
